import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { normalizeMembershipPayload } from '../utils/membership';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // 用于防止短时间内重复刷新用户信息
  const lastRefreshRef = useRef(0);

  const maybeEnsureWallet = async (userId) => {
    const pending = localStorage.getItem('wallet_export_pending');
    if (pending) return;
    try {
      const res = await api.ensureWallet();
      if (res.code === 'SUCCESS' && res.data?.created && res.data?.export_supported) {
        const payload = {
          user_id: userId || null,
          address: res.data.wallet_address,
        };
        localStorage.setItem('wallet_export_pending', JSON.stringify(payload));
      }
    } catch {
      // Ignore wallet bootstrap errors
    }
  };

  // 检查本地存储的 token
  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const expireAt = localStorage.getItem('access_token_expire_at');

    if (accessToken && expireAt && Date.now() < parseInt(expireAt) * 1000) {
      loadUserInfo();
    } else {
      tryRefreshToken();
    }
  }, []);

  const tryRefreshToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      setLoading(false);
      return;
    }

    try {
      const data = await api.refreshToken(refreshToken);
      if (data.code === 'SUCCESS') {
        saveTokens(data.data);
        await loadUserInfo();
      } else {
        clearTokens();
      }
    } catch {
      clearTokens();
    }
    setLoading(false);
  };

  const loadUserInfo = async () => {
    try {
      const data = await api.getMe();
      if (data.code === 'SUCCESS') {
        const normalizedUser = normalizeMembershipPayload(data.data);
        setUser(normalizedUser);
        lastRefreshRef.current = Date.now();
        await maybeEnsureWallet(normalizedUser?.user_id);
      } else {
        clearTokens();
      }
    } catch {
      clearTokens();
    }
    setLoading(false);
  };

  // 刷新用户信息（用于会员状态同步）
  // 返回刷新后的用户数据，如果刷新失败返回 null
  const refreshUserInfo = async () => {
    // 防止短时间内重复刷新（5秒内）
    const now = Date.now();
    if (now - lastRefreshRef.current < 5000) {
      return user;
    }
    
    try {
      const data = await api.getMe();
      if (data.code === 'SUCCESS') {
        const normalizedUser = normalizeMembershipPayload(data.data);
        setUser(normalizedUser);
        lastRefreshRef.current = now;
        await maybeEnsureWallet(normalizedUser?.user_id);
        return normalizedUser;
      }
    } catch {
      // 刷新失败，返回当前用户
    }
    return user;
  };

  const login = async (ticketId, userId, code) => {
    const data = await api.verifyLogin(ticketId, userId, code);
    if (data.code === 'SUCCESS') {
      saveTokens(data.data);
      sessionStorage.setItem('security_check_pending', data.data.user_id || userId || '');
      await loadUserInfo();
      return data.data;
    }
    throw new Error(data.msg || '登录失败');
  };

  // 直接使用 tokens 登录（用于新的轮询方式）
  const loginWithTokens = (data) => {
    saveTokens(data);
    sessionStorage.setItem('security_check_pending', data.user_id || '');
    loadUserInfo();
  };

  const logout = useCallback(() => {
    clearTokens();
    localStorage.removeItem('wallet_export_pending');
    sessionStorage.removeItem('security_check_pending');
    setUser(null);
  }, []);

  const saveTokens = (data) => {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('access_token_expire_at', data.access_token_expire_at);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('refresh_token_expire_at', data.refresh_token_expire_at);
  };

  const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('access_token_expire_at');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refresh_token_expire_at');
  };

  const updateMembership = (tierName, expiresAt) => {
    setUser((prev) =>
      normalizeMembershipPayload({
        ...prev,
        tier_name: tierName,
        raw_tier_name: tierName,
        expires_at: expiresAt,
        is_expired: false,
      })
    );
  };

  useEffect(() => {
    if (!user?.expires_at || user?.is_expired) {
      return undefined;
    }

    const expiresAt = new Date(user.expires_at);
    if (Number.isNaN(expiresAt.getTime())) {
      return undefined;
    }

    const maxTimeout = 2147483647;
    const delay = Math.max(expiresAt.getTime() - Date.now() + 1000, 0);
    const timer = window.setTimeout(() => {
      setUser((prev) => normalizeMembershipPayload(prev));
    }, Math.min(delay, maxTimeout));

    return () => window.clearTimeout(timer);
  }, [user?.expires_at, user?.is_expired, user?.raw_tier_name, user?.tier_name]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        loginWithTokens,
        logout,
        updateMembership,
        refreshUserInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
